import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon,
  CheckBadgeIcon,
  StarIcon,
  PencilSquareIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  MegaphoneIcon,
  MapPinIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  PhotoIcon,
  FilmIcon,
  Squares2X2Icon,
  PaperClipIcon,
  XMarkIcon,
  SwatchIcon,
  ChevronDownIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/24/outline';
import { Card, CardHeader, CardSection } from '../../../components/ui/Card';
import Button from '../../../ui/Button';
import Textarea from '../../../ui/Textarea';
import SelectCard from '../../../ui/SelectCard';
import { FontPicker } from '../../../domain/fonts';
import { MediaPicker } from '../../../domain/media';
import Tooltip from '../../../ui/Tooltip/Tooltip';
import { googleAdsService } from '../../../services';

const profileTypes = [
  {
    id: 'creator',
    label: 'Creator',
    description: 'Influencer, YouTuber, Produtor de Conteúdo',
    icon: SparklesIcon
  },
  {
    id: 'saas-founder',
    label: 'SaaS Founder',
    description: 'Criador de Software, Produto Digital',
    icon: RocketLaunchIcon
  },
  {
    id: 'local-business',
    label: 'Empreendedor Tradicional',
    description: 'E-commerce, Serviços, Negócio Local',
    icon: BuildingStorefrontIcon
  }
];

const platforms = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'campanha', label: 'Campanha' }
];

const strategies = [
  {
    id: 'sell-more',
    label: 'Vender Mais',
    description: 'Posts que convertem',
    icon: MegaphoneIcon
  },
  {
    id: 'authority',
    label: 'Autoridade',
    description: 'Conteúdo educativo',
    icon: LightBulbIcon
  },
  {
    id: 'local-clients',
    label: 'Clientes Locais',
    description: 'Engajamento regional',
    icon: MapPinIcon
  },
  {
    id: 'go-viral',
    label: 'Viralizar',
    description: 'Crescimento rápido',
    icon: UserGroupIcon
  }
];

const creativeModes = [
  { id: 'design', label: 'Design', icon: SwatchIcon },
  { id: 'video', label: 'Vídeo', icon: FilmIcon },
  { id: 'photo', label: 'Foto', icon: PhotoIcon },
  { id: 'carousel', label: 'Carrossel', icon: Squares2X2Icon }
];

const creativeModeHelp = {
  design: 'Gera uma arte estruturada em HTML com imagens profissionais.',
  photo: 'Gera uma imagem realista baseada na sua descrição.',
  carousel: 'Cria múltiplas páginas organizadas para publicação em sequência.',
  video: 'Gera roteiro e estrutura para vídeo com base no seu objetivo.',
};

const formatFilters = [
  { id: 'populares', label: 'Populares' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'youtube', label: 'YouTube' }
];

const creativeSizes = ['1:1', '4:5', '9:16', '16:9'];

const sizeToRatio = {
  '1:1': '1 / 1',
  '4:5': '4 / 5',
  '9:16': '9 / 16',
  '16:9': '16 / 9'
};

const visualFormatPresets = [
  {
    id: 'ig-post-45',
    title: 'Post para Instagram (4:5)',
    detail: 'Feed vertical para alcance',
    platform: 'instagram',
    mode: 'photo',
    size: '4:5',
    format: 'standard',
    resolution: '1080 × 1350 px',
    category: 'populares'
  },
  {
    id: 'ig-square',
    title: 'Post quadrado do Instagram',
    detail: 'Formato clássico de feed',
    platform: 'instagram',
    mode: 'photo',
    size: '1:1',
    format: 'standard',
    resolution: '1080 × 1080 px',
    category: 'populares'
  },
  {
    id: 'ig-reels',
    title: 'Vídeo para Reels do Instagram',
    detail: 'Vídeo vertical para descoberta',
    platform: 'instagram',
    mode: 'video',
    size: '9:16',
    format: 'premium',
    resolution: '1080 × 1920 px',
    category: 'populares'
  },
  {
    id: 'fb-post',
    title: 'Post para Facebook',
    detail: 'Feed padrão de campanha',
    platform: 'facebook',
    mode: 'photo',
    size: '1:1',
    format: 'standard',
    resolution: '1080 × 1080 px',
    category: 'facebook'
  },
  {
    id: 'fb-story',
    title: 'Story para Facebook',
    detail: 'Story vertical full-screen',
    platform: 'facebook',
    mode: 'photo',
    size: '9:16',
    format: 'standard',
    resolution: '1080 × 1920 px',
    category: 'facebook'
  },
  {
    id: 'ig-story',
    title: 'Story do Instagram',
    detail: 'Story vertical em tela cheia',
    platform: 'instagram',
    mode: 'photo',
    size: '9:16',
    format: 'standard',
    resolution: '1080 × 1920 px',
    category: 'instagram'
  },
  {
    id: 'ig-carousel',
    title: 'Carrossel do Instagram',
    detail: 'Múltiplas páginas 1:1',
    platform: 'instagram',
    mode: 'carousel',
    size: '1:1',
    format: 'premium',
    resolution: '1080 × 1080 px',
    category: 'instagram'
  },
  {
    id: 'ig-cover-reels',
    title: 'Capa para Reels',
    detail: 'Preview de perfil 1:1',
    platform: 'instagram',
    mode: 'photo',
    size: '9:16',
    format: 'premium',
    resolution: '1080 × 1920 px',
    category: 'instagram'
  },
  {
    id: 'li-post',
    title: 'Post para LinkedIn',
    detail: 'Feed profissional padrão',
    platform: 'linkedin',
    mode: 'carousel',
    size: '1:1',
    format: 'premium',
    resolution: '1080 × 1080 px',
    category: 'linkedin'
  },
  {
    id: 'li-banner',
    title: 'Banner de artigo LinkedIn',
    detail: 'Thumb horizontal para conteúdo',
    platform: 'linkedin',
    mode: 'photo',
    size: '16:9',
    format: 'standard',
    resolution: '1200 × 675 px',
    category: 'linkedin'
  },
  {
    id: 'yt-thumb',
    title: 'Thumbnail para YouTube',
    detail: 'Capa para vídeo no YouTube',
    platform: 'youtube',
    mode: 'photo',
    size: '16:9',
    format: 'premium',
    resolution: '1280 × 720 px',
    category: 'youtube'
  },
  {
    id: 'yt-shorts',
    title: 'Shorts do YouTube',
    detail: 'Vídeo vertical 9:16',
    platform: 'youtube',
    mode: 'video',
    size: '9:16',
    format: 'premium',
    resolution: '1080 × 1920 px',
    category: 'youtube'
  },
  {
    id: 'yt-community',
    title: 'Post da comunidade',
    detail: 'Post quadrado para comunidade',
    platform: 'youtube',
    mode: 'photo',
    size: '1:1',
    format: 'standard',
    resolution: '1080 × 1080 px',
    category: 'youtube'
  }
];

const profileStrategyLinks = {
  creator: ['authority', 'go-viral', 'sell-more'],
  'saas-founder': ['authority', 'sell-more'],
  'local-business': ['local-clients', 'sell-more']
};

function FeedGenerator({ state }) {
  const navigate = useNavigate();
  const linkedStrategyIds = profileStrategyLinks[state.selectedProfile] || [];
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const formatDropdownRef = useRef(null);
  const [activeFormatFilter, setActiveFormatFilter] = useState('populares');
  const [isFormatDropdownOpen, setIsFormatDropdownOpen] = useState(false);
  const [isPromotePanelOpen, setIsPromotePanelOpen] = useState(false);
  const [isLaunchingConnect, setIsLaunchingConnect] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [promoteMessage, setPromoteMessage] = useState('');
  const [campaignForm, setCampaignForm] = useState({
    type: 'demand_gen',
    budget: 50,
    audience: '',
    customerId: '',
  });

  const filteredPresets = useMemo(() => {
    if (activeFormatFilter === 'populares') {
      return visualFormatPresets.filter((preset) => preset.category === 'populares');
    }
    return visualFormatPresets.filter(
      (preset) => preset.platform === activeFormatFilter || preset.category === activeFormatFilter
    );
  }, [activeFormatFilter]);

  const selectedPreset = useMemo(
    () =>
      visualFormatPresets.find(
        (preset) =>
          preset.mode === state.creativeMode &&
          preset.size === state.creativeSize &&
          preset.format === state.creativeFormat
      ) || null,
    [state.creativeMode, state.creativeSize, state.creativeFormat]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!formatDropdownRef.current?.contains(event.target)) {
        setIsFormatDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openPromotePanel = async () => {
    setPromoteMessage('');
    setIsLaunchingConnect(true);

    try {
      const status = await googleAdsService.getStatus();
      if (status?.success && status?.connected) {
        setCampaignForm((previous) => ({
          ...previous,
          customerId: status?.account?.google_ads_customer_id || previous.customerId,
        }));
        setIsPromotePanelOpen(true);
        return;
      }

      const connection = await googleAdsService.getConnectUrl();
      if (connection?.success && connection?.auth_url) {
        window.location.assign(connection.auth_url);
        return;
      }

      setPromoteMessage('Conecte uma conta Google Ads antes de promover o conteúdo.');
    } catch (error) {
      console.error(error);
      setPromoteMessage('Não foi possível validar a conexão com Google Ads.');
    } finally {
      setIsLaunchingConnect(false);
    }
  };

  const submitCampaign = async () => {
    if (!state.generatedPost) return;

    setPromoteMessage('');
    setIsCreatingCampaign(true);

    try {
      const mediaUrl =
        state.generatedPost?.image_url ||
        state.generatedPost?.design?.media_url ||
        state.generatedPost?.design?.background_image_url ||
        '';

      const postId =
        state.generatedPost?.design?.id ||
        state.generatedPost?.id ||
        null;

      const payload = {
        type: campaignForm.type,
        budget: Number(campaignForm.budget),
        audience: campaignForm.audience,
        customer_id: campaignForm.customerId || undefined,
        post_id: postId || undefined,
        media_url: mediaUrl || undefined,
        media_type: state.creativeMode === 'video' ? 'video' : 'photo',
        name: `Promoção ${campaignForm.type === 'demand_gen' ? 'Demand Gen' : 'Performance Max'} ${new Date().toLocaleDateString('pt-BR')}`,
      };

      const response = await googleAdsService.createCampaign(payload);
      if (!response?.success) {
        setPromoteMessage(response?.error || response?.message || 'Não foi possível criar a campanha.');
        return;
      }

      setPromoteMessage('Campanha criada com sucesso no Google Ads.');
      setIsPromotePanelOpen(false);
    } catch (error) {
      console.error(error);
      setPromoteMessage('Erro ao criar campanha no Google Ads.');
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  return (
    <div className="feed-page__layout">
      <section className="feed-page__builder">
        <div className="feed-page__energy">Energia disponível: {state.userEnergy}</div>

        <div className="feed-page__hero">
          <Tooltip
            variant="tip"
            icon="✦"
            content="Use o Criar com IA para transformar ideias em peças completas com texto, visual e estratégia."
            block
          >
            <h2>Crie posts que vendem em segundos com IA</h2>
          </Tooltip>
          <p>Personalize seu Prompt IA conectando seu perfil com a estratégia desejada.</p>

          <div className="feed-page__hero-icons" aria-hidden="true">
            <span><CheckBadgeIcon /></span>
            <span><SparklesIcon /></span>
            <span><StarIcon /></span>
            <span><PencilSquareIcon /></span>
          </div>
        </div>

        {state.error && <div className="feed-page__error">{state.error}</div>}

        <div className="feed-page__prompt-row">
          <div className="feed-page__composer">
            <div className="feed-page__composer-modes" role="tablist" aria-label="Tipo de criativo">
              {creativeModes.map((mode) => {
                const Icon = mode.icon;
                const active = state.creativeMode === mode.id;

                return (
                  <Tooltip key={mode.id} content={creativeModeHelp[mode.id]} variant="info" block>
                    <button
                      type="button"
                      className={`feed-page__mode-btn ${active ? 'is-active' : ''}`}
                      onClick={() => state.handleCreativeModeChange(mode.id)}
                      role="tab"
                      aria-selected={active}
                    >
                      <Icon />
                      {mode.label}
                    </button>
                  </Tooltip>
                );
              })}
            </div>

            <Tooltip
              content="Descreva com clareza o que você deseja criar. Quanto mais detalhado, melhor será o resultado da IA."
              variant="tip"
              block
            >
              <Textarea
                rows={4}
                value={state.aiPrompt}
                onChange={(event) => state.setAiPrompt(event.target.value)}
                placeholder="Descreva o criativo, objetivo e referências. Você pode anexar arquivos abaixo."
              />
            </Tooltip>

            {state.attachedFiles.length > 0 && (
              <div className="feed-page__attachments">
                {state.attachedFiles.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="feed-page__attachment-chip">
                    <PaperClipIcon />
                    <span>{file.name}</span>
                    <button
                      type="button"
                      onClick={() => state.removeAttachedFile(index)}
                      aria-label={`Remover ${file.name}`}
                    >
                      <XMarkIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="feed-page__composer-tools">
              <button
                type="button"
                className="feed-page__tool-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <PaperClipIcon />
                Anexar arquivos
              </button>

              <Tooltip
                content="Envie sua logo para aplicar identidade da marca automaticamente nas peças geradas."
                variant="tip"
              >
                <button
                  type="button"
                  className="feed-page__tool-btn"
                  onClick={() => logoInputRef.current?.click()}
                >
                  <SwatchIcon />
                  {state.logoImage ? 'Logo anexada' : 'Upload de logo'}
                </button>
              </Tooltip>

              <div className="feed-page__tool-dropdown" ref={formatDropdownRef}>
                <Tooltip
                  content="Escolha o formato de conteúdo para adaptar estrutura, tamanho e objetivo da publicação."
                  variant="info"
                >
                  <button
                    type="button"
                    className="feed-page__tool-btn feed-page__tool-btn--format"
                    onClick={() => setIsFormatDropdownOpen((previous) => !previous)}
                    aria-expanded={isFormatDropdownOpen}
                  >
                    <SwatchIcon />
                    {selectedPreset ? selectedPreset.title : 'Escolher formato'}
                    <ChevronDownIcon className={isFormatDropdownOpen ? 'is-open' : ''} />
                  </button>
                </Tooltip>

                {isFormatDropdownOpen && (
                  <div className="feed-page__format-browser">
                    <div className="feed-page__format-tabs" role="tablist" aria-label="Categorias de formato">
                      {formatFilters.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          role="tab"
                          aria-selected={activeFormatFilter === filter.id}
                          className={`feed-page__format-tab ${activeFormatFilter === filter.id ? 'is-active' : ''}`}
                          onClick={() => setActiveFormatFilter(filter.id)}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>

                    <div className="feed-page__format-list">
                      {filteredPresets.map((preset) => {
                        const isSelected =
                          state.creativeMode === preset.mode &&
                          state.creativeSize === preset.size &&
                          state.creativeFormat === preset.format;

                        return (
                          <button
                            key={preset.id}
                            type="button"
                            className={`feed-page__format-card ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => {
                              state.handleCreativeModeChange(preset.mode);
                              state.setCreativeSize(preset.size);
                              state.setCreativeFormat(preset.format);
                              setIsFormatDropdownOpen(false);
                            }}
                          >
                            <span
                              className={`feed-page__format-thumb feed-page__format-thumb--${preset.mode}`}
                              style={{ '--feed-format-ratio': sizeToRatio[preset.size] || '1 / 1' }}
                            />
                            <span className="feed-page__format-title">{preset.title}</span>
                            <span className="feed-page__format-detail">{preset.detail}</span>
                            <span className="feed-page__format-meta">{preset.size} • {preset.resolution}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <Tooltip content="Defina o tamanho ideal conforme o canal em que o conteúdo será publicado." variant="info">
                <label className="feed-page__tool-select">
                  <ArrowsPointingOutIcon />
                  <select
                    value={state.creativeSize}
                    onChange={(event) => state.setCreativeSize(event.target.value)}
                  >
                    {creativeSizes.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </label>
              </Tooltip>

              <FontPicker
                label="Fonte"
                value={state.selectedFont}
                onChange={state.setSelectedFont}
              />

              {(state.creativeMode === 'design' || state.creativeMode === 'carousel' || state.creativeMode === 'photo' || state.creativeMode === 'video') && (
                <div className="feed-page__image-picker-wrap">
                  <MediaPicker type="photo" value={state.selectedPhotoMedia} onChange={state.setSelectedPhotoMedia} />
                </div>
              )}

              {(state.creativeMode === 'design' || state.creativeMode === 'carousel' || state.creativeMode === 'photo' || state.creativeMode === 'video') && (
                <div className="feed-page__image-picker-wrap">
                  <MediaPicker type="video" value={state.selectedVideoMedia} onChange={state.setSelectedVideoMedia} />
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              className="feed-page__file-input"
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
              onChange={(event) => {
                state.attachFiles(event.target.files);
                event.target.value = '';
              }}
            />

            <input
              ref={logoInputRef}
              className="feed-page__file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = () => state.setLogoImage(String(reader.result || ''));
                reader.readAsDataURL(file);
                event.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="feed-page__selection-grid">
            <section>
              <h4>SEU PERFIL</h4>
              <div className="feed-page__selection-list">
                {profileTypes.map((profile) => {
                  const Icon = profile.icon;
                  const isActive = state.selectedProfile === profile.id;

                  return (
                    <SelectCard
                      key={profile.id}
                      selected={isActive}
                      className="feed-page__select-card"
                      onClick={() => state.selectProfile(profile.id, linkedStrategyIds[0])}
                    >
                      <div className="feed-page__select-row">
                        <Icon className="feed-page__select-icon" />
                        <div>
                          <strong>{profile.label}</strong>
                          <small>{profile.description}</small>
                        </div>
                      </div>
                    </SelectCard>
                  );
                })}
              </div>
            </section>

            <section>
              <h4>SUA ESTRATÉGIA</h4>
              <div className="feed-page__selection-list">
                {strategies.map((strategy) => {
                  const Icon = strategy.icon;
                  const isEnabled = linkedStrategyIds.includes(strategy.id);
                  const isActive = state.selectedStrategy === strategy.id;

                  return (
                    <SelectCard
                      key={strategy.id}
                      selected={isActive}
                      className={`feed-page__select-card ${!isEnabled ? 'is-muted' : ''}`}
                      onClick={() => state.selectStrategy(strategy.id)}
                      disabled={!isEnabled}
                    >
                      <div className="feed-page__select-row">
                        <Icon className="feed-page__select-icon" />
                        <div>
                          <strong>{strategy.label}</strong>
                          <small>{strategy.description}</small>
                        </div>
                      </div>
                    </SelectCard>
                  );
                })}
              </div>
            </section>
        </div>

        <Tooltip
          content="Selecione a plataforma principal para ajustar linguagem, formato e recomendação de publicação."
          variant="info"
          block
        >
          <div className="feed-page__platforms">
            {platforms.map((platform) => (
              <div key={platform.id} className="feed-page__platform-chip">
                <SelectCard
                  selected={state.selectedPlatforms.includes(platform.id)}
                  onClick={() => state.togglePlatformSelection(platform.id)}
                >
                  {platform.label}
                </SelectCard>
              </div>
            ))}
          </div>
        </Tooltip>

        <div className="feed-page__footer">
          <Tooltip
            content="Clique para gerar com IA usando o prompt, formato e plataforma selecionados."
            variant="tip"
          >
            <Button
              variant="primary"
              className="feed-page__cta feed-page__cta--compact"
              onClick={state.generatePost}
              disabled={!state.canGenerate || state.isGenerating}
            >
              {state.isGenerating
                ? 'Gerando...'
                : state.creativeMode === 'design' || state.creativeMode === 'carousel'
                  ? 'Gerar Design com IA'
                  : 'Gerar Post com IA'}
            </Button>
          </Tooltip>
        </div>
      </section>

      {state.isGenerating && (
        <div className="feed-page__loading-overlay" role="status" aria-live="polite">
          <div className="feed-page__loading-card">
            <span className="feed-page__loading-gif" aria-hidden="true">
              <img src="/eddy-loader.gif" alt="" />
            </span>
            <strong>Eddy ligando a máquina...</strong>
            <small>Gerando seu criativo com IA</small>
          </div>
        </div>
      )}

      {state.generatedPost && (
        <Card className="feed-page__result-card">
          <CardHeader title={state.generatedPost?.kind === 'design' ? 'Design Gerado' : 'Resultado'} />
          <CardSection>
            {state.generatedPost?.image_url && (
              <img className="feed-page__result-image" src={state.generatedPost.image_url} alt="Conteúdo gerado" />
            )}
            {state.generatedPost?.content && (
              <pre className="feed-page__result-text">{state.generatedPost.content}</pre>
            )}
            {(state.generatedPost?.kind === 'design' || state.generatedPost?.kind === 'legacy-design') && (
              <div className="feed-page__result-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const designId = state.generatedPost?.design?.id;
                    navigate(designId ? `/manual-create?designId=${designId}` : '/manual-create');
                  }}
                >
                  Editar agora
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/contents')}
                >
                  Editar depois
                </button>
              </div>
            )}

            <div className="feed-page__result-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openPromotePanel}
                disabled={isLaunchingConnect || isCreatingCampaign}
              >
                {isLaunchingConnect ? 'Verificando Google Ads...' : 'Promover com Google Ads'}
              </button>
            </div>

            {isPromotePanelOpen && (
              <div className="feed-page__promote-panel">
                <h4>Nova campanha Google Ads</h4>

                <label className="feed-page__promote-label">
                  Tipo de campanha
                  <select
                    value={campaignForm.type}
                    onChange={(event) => setCampaignForm((previous) => ({ ...previous, type: event.target.value }))}
                  >
                    <option value="demand_gen">Demand Gen</option>
                    <option value="performance_max">Performance Max</option>
                  </select>
                </label>

                <label className="feed-page__promote-label">
                  Budget diário (R$)
                  <input
                    type="number"
                    min="1"
                    value={campaignForm.budget}
                    onChange={(event) => setCampaignForm((previous) => ({ ...previous, budget: event.target.value }))}
                  />
                </label>

                <label className="feed-page__promote-label">
                  Audiência
                  <input
                    type="text"
                    placeholder="Ex: empreendedores digitais"
                    value={campaignForm.audience}
                    onChange={(event) => setCampaignForm((previous) => ({ ...previous, audience: event.target.value }))}
                  />
                </label>

                <label className="feed-page__promote-label">
                  Customer ID (opcional)
                  <input
                    type="text"
                    placeholder="1234567890"
                    value={campaignForm.customerId}
                    onChange={(event) => setCampaignForm((previous) => ({ ...previous, customerId: event.target.value }))}
                  />
                </label>

                <div className="feed-page__promote-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsPromotePanelOpen(false)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitCampaign}
                    disabled={isCreatingCampaign}
                  >
                    {isCreatingCampaign ? 'Criando campanha...' : 'Criar Campanha'}
                  </button>
                </div>
              </div>
            )}

            {promoteMessage && <p className="feed-page__promote-message">{promoteMessage}</p>}
          </CardSection>
        </Card>
      )}
    </div>
  );
}

export default FeedGenerator;
